/**
 * WebSocket client implementation
 */

import type {
  WSConnection,
  WSMessageHandler,
  WSErrorHandler,
  WSCloseHandler,
  WSOpenHandler,
  ClientConfig,
} from './types';

/**
 * WebSocket connection implementation
 */
class WSConnectionImpl<T = any> implements WSConnection<T> {
  private ws: WebSocket | null = null;
  private openHandlers: WSOpenHandler[] = []; // Added openHandlers property
  private messageHandlers: WSMessageHandler<T>[] = [];
  private errorHandlers: WSErrorHandler[] = [];
  private closeHandlers: WSCloseHandler[] = [];
  private url: string;

  constructor(url: string, config: ClientConfig) {
    // Build full URL
    const baseURL = config.baseURL.replace(/^http/, 'ws'); // Convert http(s) to ws(s)
    this.url = `${baseURL}${url}`;

    // Add token to URL if available
    if (config.getToken) {
      const token = config.getToken();
      if (token) {
        const separator = this.url.includes('?') ? '&' : '?';
        this.url = `${this.url}${separator}token=${encodeURIComponent(token)}`;
      }
    }
  }

  /**
   * Get current connection state
   */
  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  /**
   * Check if connection is open
   */
  get connected(): boolean {
    return this.readyState === WebSocket.OPEN;
  }

  /**
   * Register open handler
   */
  onOpen(handler: WSOpenHandler): void {
    this.openHandlers.push(handler);
  }

  /**
   * Register message handler
   */
  on(handler: WSMessageHandler<T>): void {
    this.messageHandlers.push(handler);
  }

  /**
   * Register error handler
   */
  onError(handler: WSErrorHandler): void {
    this.errorHandlers.push(handler);
  }

  /**
   * Register close handler
   */
  onClose(handler: WSCloseHandler): void {
    this.closeHandlers.push(handler);
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.ws) {
      throw new Error('WebSocket is already connected');
    }

    this.ws = new WebSocket(this.url);

    // Setup event listeners
    this.ws.onopen = (event) => {
      // Added onopen event listener
      this.openHandlers.forEach((handler) => handler(event));
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.messageHandlers.forEach((handler) => handler(data));
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onerror = (event) => {
      this.errorHandlers.forEach((handler) => handler(event));
    };

    this.ws.onclose = (event) => {
      this.closeHandlers.forEach((handler) => handler(event));
      this.ws = null;
    };
  }

  /**
   * Send message to server
   */
  send(data: any): void {
    if (!this.ws) {
      throw new Error('WebSocket is not connected. Call connect() first.');
    }

    if (this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not open. Current state: ' + this.ws.readyState);
    }

    const message = typeof data === 'string' ? data : JSON.stringify(data);
    this.ws.send(message);
  }

  /**
   * Close connection
   */
  close(code?: number, reason?: string): void {
    // Modified close method signature
    if (this.ws) {
      this.ws.close(code, reason); // Modified close call
      this.ws = null;
    }
  }
}

/**
 * Create WebSocket connection
 */
export function createWSConnection<T = any>(path: string, config: ClientConfig): WSConnection<T> {
  return new WSConnectionImpl<T>(path, config);
}
